import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy, inject } from "@angular/core";
import { JsonConvert } from "json2typescript";

import { Observable, Subscription } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import {
    AddToCartRequest,
    AddToCartResponse,
    Basket,
    BasketItemRequest,
    CustomOptions,
    DeliveryAddressRequest,
    DeliveryAddressResponse,
    DeliveryShippingRequest,
    DeliveryShippingResponse,
    ErrorItem,
    RemoveFromCartRequest,
    RemoveFromCartResponse,
    SetCartItemRequest,
    SetCartItemResponse,
} from "@hermes/api-model-basket";
import { LOCALE, Settings, StorageService } from "@hermes/core-app-core";

import { VendorTrackingService } from "@hermes/hermes-web-fragments-vendor-tracking";
import { AppointmentsSignalStore } from "@hermes/hermes-web-states-appointments-state";
import {
    HTTP_JSON_CONTENT_TYPE_HEADER,
    SKIP_404_INTERCEPTOR,
} from "@hermes/utils-generic-constants";

import { RemoteAppointmentIdStorageService } from "./remote-appointment-id-storage.service";

@Injectable()
export class CartService implements OnDestroy {
    readonly #storageService = inject(StorageService);
    readonly #appointmentIdStorage = inject(RemoteAppointmentIdStorageService);
    readonly #http = inject(HttpClient);
    readonly #settings = inject(Settings);
    readonly #vendorIdTracking = inject(VendorTrackingService);
    readonly #appointmentStore = inject(AppointmentsSignalStore, {
        optional: true,
    });
    readonly #locale = inject(LOCALE);
    readonly #subscription = new Subscription();

    public ngOnDestroy(): void {
        this.#subscription.unsubscribe();
    }

    public addToCart(
        addToCartRequest: AddToCartRequest,
    ): Observable<AddToCartResponse> {
        return this.#http
            .post<AddToCartResponse>(
                `${this.#settings.apiUrl}/add-to-cart`,
                addToCartRequest,
                {
                    headers: new HttpHeaders({
                        ...HTTP_JSON_CONTENT_TYPE_HEADER,
                    }),
                    context: new HttpContext().set(SKIP_404_INTERCEPTOR, true),
                },
            )
            .pipe(
                tap((response) => {
                    if (!response.error) {
                        this.#vendorIdTracking.cleanVendorId();
                        this.#appointmentIdStorage.clear();
                    }
                }),
            );
    }

    public addProductToCart(
        sku: string,
        skuSet?: string,
        dku?: string,
        urlImage?: string,
        customOptions?: CustomOptions,
    ): Observable<AddToCartResponse> {
        const backInStockSku: string | undefined =
            this.#storageService.getSessionStorageItem("productSkuBackInStock");

        const vendorId = this.#vendorIdTracking.getVendorId();
        const resolvedAppointmentId = this.#resolveAppointmentId();
        const request: AddToCartRequest = {
            locale: this.#locale.code,
            items: new JsonConvert().serializeArray(
                [
                    {
                        category: "direct",
                        container_id: skuSet,
                        sku,
                        image: urlImage,
                        dku,
                        customOptions,
                    },
                ],
                BasketItemRequest,
            ),
            ...(backInStockSku && {
                trackingInformation: {
                    feature: "back_in_stock",
                    information: backInStockSku,
                },
            }),
            ...(vendorId && { sid: vendorId }),
            ...(resolvedAppointmentId && {
                appointmentId: resolvedAppointmentId,
            }),
        };

        return this.addToCart(request);
    }

    #resolveAppointmentId(): string | undefined {
        return (
            this.#appointmentStore?.fetchCurrentAppointmentId() ??
            this.#appointmentIdStorage.get()
        );
    }

    public addManyProductsToCart(
        skus: readonly string[],
    ): Observable<AddToCartResponse> {
        const vendorId = this.#vendorIdTracking.getVendorId();

        const request: AddToCartRequest = {
            locale: this.#locale.code,
            items: new JsonConvert().serializeArray(
                skus.map((sku) => ({
                    sku,
                })),
                BasketItemRequest,
            ),
            ...(vendorId && { sid: vendorId }),
        };

        return this.addToCart(request);
    }

    public updateShippingMethod(
        deliveryShippingRequest: DeliveryShippingRequest,
    ): Observable<DeliveryShippingResponse> {
        return this.#http
            .post<DeliveryShippingResponse>(
                `${this.#settings.apiUrl}/delivery/shipping`,
                deliveryShippingRequest,
                {
                    headers: new HttpHeaders({
                        ...HTTP_JSON_CONTENT_TYPE_HEADER,
                    }),
                    context: new HttpContext().set(SKIP_404_INTERCEPTOR, true),
                },
            )
            .pipe(
                map((data: { basket: Basket; error?: ErrorItem }) =>
                    this.deserializeBasketFromData<DeliveryShippingResponse>(
                        data,
                    ),
                ),
                catchError((response) => {
                    if (
                        response.error.message ===
                        "Address is not valid for Fedex"
                    ) {
                        throw response.error;
                    }
                    throw this.errorHandler("update shipping error");
                }),
            );
    }

    public updateAddress(
        deliveryAddressRequest: DeliveryAddressRequest,
    ): Observable<DeliveryAddressResponse> {
        return this.#http
            .post<DeliveryAddressResponse>(
                `${this.#settings.apiUrl}/delivery/address`,
                deliveryAddressRequest,
                {
                    headers: new HttpHeaders({
                        ...HTTP_JSON_CONTENT_TYPE_HEADER,
                    }),
                    context: new HttpContext().set(SKIP_404_INTERCEPTOR, true),
                },
            )
            .pipe(
                map((data: { basket: Basket; error?: ErrorItem }) =>
                    this.deserializeBasketFromData<DeliveryAddressResponse>(
                        data,
                    ),
                ),
            );
    }

    public deserializeBasketFromData<
        T extends { basket: Basket; error?: ErrorItem },
    >(data: { basket: Basket; error?: ErrorItem }): T {
        if (data.basket) {
            data.basket = new JsonConvert().deserializeObject(
                data.basket,
                Basket,
            );
        }
        if (data.error) {
            data.error = new JsonConvert().deserializeObject(
                data.error,
                ErrorItem,
            );
        }
        return data as unknown as T;
    }

    /**
     * call set cart item request from skus
     * @param sku product's sku to set to cart
     * @param qty new quantity of the product in cart
     * @param customOptions product's engraving information
     */
    public setCartItemProduct(
        sku: string,
        qty: number,
        customOptions?: CustomOptions,
    ): Observable<SetCartItemResponse> {
        const request: SetCartItemRequest = {
            locale: this.#locale.code,
            items: new JsonConvert().serializeArray(
                [
                    {
                        qty,
                        sku,
                        customOptions,
                    },
                ],
                BasketItemRequest,
            ),
        };
        return this.setCartItem(request);
    }

    public removeProductsFromCart(
        items: Array<{
            sku: string;
            qty: number;
            customOptions?: CustomOptions;
        }>,
    ): Observable<RemoveFromCartResponse> {
        const request: RemoveFromCartRequest = {
            locale: this.#locale.code,
            items: new JsonConvert().serializeArray(items, BasketItemRequest),
        };
        return this.removeFromCart(request);
    }

    private errorHandler(message: string) {
        return (error: { error: unknown }) => {
            throw new Error(`${message} : ${error.error}`);
        };
    }

    private setCartItem(
        setCartItemRequest: SetCartItemRequest,
    ): Observable<SetCartItemResponse> {
        return this.#http
            .post<SetCartItemResponse>(
                `${this.#settings.apiUrl}/set-cart-item`,
                setCartItemRequest,
                {
                    headers: new HttpHeaders({
                        ...HTTP_JSON_CONTENT_TYPE_HEADER,
                    }),
                    context: new HttpContext().set(SKIP_404_INTERCEPTOR, true),
                },
            )
            .pipe(catchError(this.errorHandler("Set cart item error")));
    }

    private removeFromCart(
        removeFromCartRequest: RemoveFromCartRequest,
    ): Observable<RemoveFromCartResponse> {
        return this.#http
            .post<RemoveFromCartResponse>(
                `${this.#settings.apiUrl}/remove-from-cart`,
                removeFromCartRequest,
                {
                    headers: new HttpHeaders({
                        ...HTTP_JSON_CONTENT_TYPE_HEADER,
                    }),
                    context: new HttpContext().set(SKIP_404_INTERCEPTOR, true),
                },
            )
            .pipe(catchError(this.errorHandler("Remove from cart error")));
    }
}
